import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  TextField,
  MenuItem,
  Modal,
  Select, Container,
  InputLabel,
  FormControl,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";

const AddVendor = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const { vendorID } = useParams();
  const { state } = useLocation();
  const encodedFormId =
    state?.encodedFormId || sessionStorage.getItem("encodedFormId");
  const isEditMode = !!vendorID;

  const [formData, setFormData] = useState({
    vendorID: isEditMode ? parseInt(vendorID) : 0,
    vendorName: "",
    gstin: "",
    contact: "",
    address: "",
    branchId: 0,
  });
  const [branches, setBranches] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [branchError, setBranchError] = useState("");

  useEffect(() => {
    if (!encodedFormId) {
      setError("Form ID is missing. Redirecting...");
      setTimeout(() => navigate(-1), 2000);
    }
  }, [encodedFormId, navigate]);

  const fetchBranches = async (retryCount = 0) => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setBranches(responseData || []);
        setBranchError("");

        if (responseData?.length === 1) {
          const onlyBranch = responseData[0];
          setFormData((prev) => ({
            ...prev,
            branchId: onlyBranch.tblBranchId,
          }));
        }
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch branches:", response.status, errorText);
        if (retryCount < 2) {
          setTimeout(() => fetchBranches(retryCount + 1), 2000);
        } else {
          setBranchError("Failed to load branches. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      if (retryCount < 2) {
        setTimeout(() => fetchBranches(retryCount + 1), 2000);
      } else {
        setBranchError("An error occurred while loading branches.");
      }
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const fetchVendor = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Inventory/Vendor/GetVendors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({ vendorId: parseInt(vendorID) }),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.vendors && responseData.vendors.length > 0) {
          const vendor = responseData.vendors[0];
          setFormData({
            vendorID: vendor.vendorID,
            vendorName: vendor.vendorName,
            gstin: vendor.gstin,
            contact: vendor.contact,
            address: vendor.address,
            branchId: vendor.tblBranchId || vendor.branchId || 0,
          });
        } else {
          console.error("No vendor data found for vendorID:", vendorID);
          setError("Vendor data not found.");
        }
      } else {
        console.error(
          "Failed to fetch vendor:",
          response.status,
          response.statusText
        );
        setError("Failed to load vendor details.");
      }
    } catch (error) {
      console.error("Error fetching vendor:", error);
      setError("An error occurred while loading vendor details.");
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

const handleReset = () => {
  setFormData({
    vendorID: isEditMode ? parseInt(vendorID) : 0,
    vendorName: "",
    gstin: "",
    contact: "",
    address: "",
   
  });
  
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const payload = {
        vendorID: parseInt(formData.vendorID),
        vendorName: formData.vendorName,
        gstin: formData.gstin,
        contact: formData.contact,
        address: formData.address,
        branchId: parseInt(formData.branchId),
      };
      console.log("Submitting payload:", payload);

      const response = await fetch(
        `${apiUrl}/Inventory/Vendor/InsertUpdateVendor`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("API response:", responseData);
        setShowSuccessModal(true);
      } else {
        const errorText = await response.text();
        console.error("Failed to save vendor:", response.status, errorText);
        setError(`Failed to save vendor: ${errorText || response.statusText}`);
      }
    } catch (error) {
      console.error("Error saving vendor:", error);
      setError("An error occurred while saving the vendor.");
    } finally {
      setLoadingBarProgress(100);
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRetryBranches = () => {
    setBranchError("");
    setIsLoading(true);
    fetchBranches();
  };

  useEffect(() => {
    if (branches.length === 1 && !formData.branchId) {
      const branch = branches[0];
      setFormData((prev) => ({
        ...prev,
        branchId: branch.branchId,
      }));
    }
  }, [branches, formData.branchId]);

  useEffect(() => {
    if (encodedFormId) {
      setIsLoading(true);
      fetchBranches();
      if (isEditMode) {
        fetchVendor();
      }
    }
  }, [isEditMode, vendorID, encodedFormId]);

  if (!encodedFormId) {
    return (
      <Box sx={{ padding: 4 }}>
        <Typography color="error">
          {error || "Form ID is missing. Redirecting..."}
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />

      {/* Header Box */}
   <Box
          sx={{
            backgroundColor: '#0B1F40', // dark navy blue
            padding: '12px 24px',
            boxShadow: 3,
            borderRadius: '4px 4px 0 0',
            margin: '2px auto',
           maxWidth: "100%",
          }}
        >
          <Typography variant="h4" sx={{ color: '#fff',  fontWeight: 'bold' }}>
        Add Vendor
      </Typography> </Box>


      <Paper elevation={2} sx={{ padding: 4, borderRadius: "8px", marginTop:2 }}>
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Vendor Name"
            name="vendorName"
            value={formData.vendorName}
            onChange={handleChange}
            margin="normal"
            required
            sx={{ borderRadius: "6px" }}
          />
          <TextField
            fullWidth
            label="GSTIN"
            name="gstin"
            value={formData.gstin}
            onChange={handleChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Contact"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            margin="normal"
            inputProps={{maxLength:"10"}}
          />
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={3}
          />

          <FormControl fullWidth margin="normal" error={!!branchError}>
            <InputLabel>Branch</InputLabel>
            <Select
              name="branchId"
              value={formData.branchId}
              onChange={handleChange}
              label="Branch"
              disabled={!!branchError || branches.length === 1}
            >
              {branches.length > 0 ? (
                branches.map((branch) => (
                  <MenuItem key={branch.branchId} value={branch.branchId}>
                    {branch.branchName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No branches available</MenuItem>
              )}
            </Select>
            {branchError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {branchError}
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleRetryBranches}
                  sx={{ ml: 1 }}
                >
                  Retry
                </Button>
              </Typography>
            )}
          </FormControl>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold',
              marginRight:2

              }}
              disabled={isSubmitting || !!branchError}
            >
              SAVE
            </Button>
            <Button
              variant="contained"
              color="error"
             sx={{
                width: "80px",
              height: "50px",
              padding: "10px 0",
              fontWeight:'bold'

              }}
              onClick={handleReset}
              disabled={isSubmitting}
            >
              RESET
            </Button>
          </Box>
        </form>
      </Paper>

      <Modal
  open={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
        Vendor Added ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The Vendor has been added successfully.
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1); // go back after closing modal
          }}
          variant="contained"
          color="primary"
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

    </Container>
  );
};

export default AddVendor;
